import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Lock, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [valid, setValid] = useState(null);  // null=checking, true, false
    const [email, setEmail] = useState("");
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/auth/reset-password/validate/${token}`);
                setValid(!!data?.valid);
                if (data?.email) setEmail(data.email);
            } catch {
                setValid(false);
            }
        })();
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 8) return toast.error("La contraseña debe tener al menos 8 caracteres");
        if (pwd !== confirm) return toast.error("Las contraseñas no coinciden");
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: pwd, confirm_password: confirm });
            setDone(true);
            setTimeout(() => navigate("/login", { replace: true }), 3000);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No se pudo actualizar la contraseña");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-100 p-6" data-testid="reset-password-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-6 justify-center">
                    <div className="w-10 h-10 bg-red-600 rounded-sm flex items-center justify-center font-display font-black text-lg text-white">F</div>
                    <div>
                        <div className="font-display font-black text-xl">FindMed</div>
                        <div className="text-[10px] uppercase tracking-widest text-slate-500">Dispatch Control</div>
                    </div>
                </div>

                {valid === null && (
                    <div className="fm-card p-6 text-center text-sm text-slate-500 font-mono">Validando enlace...</div>
                )}

                {valid === false && (
                    <div className="fm-card p-6 border-l-4 border-l-red-500" data-testid="reset-invalid">
                        <AlertTriangle className="w-10 h-10 text-red-600 mb-3" strokeWidth={1.5} />
                        <h2 className="font-display font-bold text-2xl mb-2">Enlace inválido o expirado</h2>
                        <p className="text-sm text-slate-600 mb-5">
                            Este enlace ya fue usado o expiró (los enlaces son válidos por 60 minutos).
                            Solicita uno nuevo para continuar.
                        </p>
                        <Link to="/forgot-password" className="fm-btn-primary w-full justify-center">
                            Solicitar enlace nuevo
                        </Link>
                    </div>
                )}

                {valid === true && done && (
                    <div className="fm-card p-6 border-l-4 border-l-emerald-500" data-testid="reset-done">
                        <CheckCircle2 className="w-10 h-10 text-emerald-600 mb-3" strokeWidth={1.5} />
                        <h2 className="font-display font-bold text-2xl mb-2">¡Contraseña actualizada!</h2>
                        <p className="text-sm text-slate-600 mb-3">
                            Ya puedes iniciar sesión con tu nueva contraseña. Te redirigimos al login...
                        </p>
                        <Link to="/login" className="fm-btn-primary w-full justify-center">
                            Ir al inicio de sesión
                        </Link>
                    </div>
                )}

                {valid === true && !done && (
                    <form onSubmit={submit} className="fm-card p-6">
                        <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold mb-2">RECUPERAR ACCESO</div>
                        <h2 className="font-display font-bold text-2xl mb-1">Crea tu nueva contraseña</h2>
                        {email && (
                            <p className="text-xs text-slate-500 mb-5 font-mono">Cuenta: {email}</p>
                        )}

                        <label className="fm-label">Nueva contraseña</label>
                        <div className="relative mb-3">
                            <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={pwd}
                                onChange={(e) => setPwd(e.target.value)}
                                className="fm-input pl-9"
                                placeholder="Mínimo 8 caracteres"
                                data-testid="reset-new-pwd"
                            />
                        </div>

                        <label className="fm-label">Confirmar contraseña</label>
                        <div className="relative">
                            <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                            <input
                                type="password"
                                required
                                minLength={8}
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                className="fm-input pl-9"
                                placeholder="Repite la contraseña"
                                data-testid="reset-confirm-pwd"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="fm-btn-primary w-full justify-center mt-5"
                            data-testid="reset-submit-btn"
                        >
                            {loading ? "Guardando..." : "Actualizar contraseña"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
